import { Link as RouterLink } from 'react-router-dom'
import { formatDate, formatRelativeTime } from '@core/utils/format'
import { getAvatarColor } from '@/lib/utils/avatar'
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  VStack,
  HStack,
  Flex,
  Input,
  InputGroup,
  InputLeftAddon,
  SimpleGrid,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Icon,
  List,
  ListItem,
  Divider,
  useColorMode,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Checkbox,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from '@chakra-ui/react'
import { Key, Copy, Check, Trash2, AlertTriangle, Pencil, Ghost, Eye, EyeOff, Cpu, ExternalLink, Zap, FileText, Clock, Activity } from 'lucide-react'
import { UsageBar, StorageBar } from '@/components/common/UsageBar'
import { AgentConnectionRow, CursorConnectButton, ClaudeConnectButton } from '@/components/agents'
import { useSettingsAccount } from './useSettingsAccount'

export function SettingsPage() {
  const { colorMode } = useColorMode()

  const {
    view,
    apiKey,
    inputKey,
    setInputKey,
    stats,
    loading,
    error,
    newKeyData,
    signUpName,
    setSignUpName,
    signUpError,
    setSignUpError,
    acceptedTerms,
    setAcceptedTerms,
    termsError,
    setTermsError,
    hasCopied,
    onCopy,
    // Name editing
    isEditingName,
    editedName,
    setEditedName,
    savingName,
    handleStartEditName,
    handleSaveName,
    handleCancelEditName,
    // Revoke modal
    isRevokeOpen,
    onRevokeOpen,
    onRevokeClose,
    // Secret key display
    showSecretKey,
    setShowSecretKey,
    hasSecretKeyCopied,
    onSecretKeyCopy,
    maskSecretKey,
    // Agents
    agents,
    loadingAgents,
    // Handlers
    handleSignUp,
    handleSignUpComplete,
    handleGetStarted,
    handleValidateKey,
    handleRevokeApiKey,
  } = useSettingsAccount()

  // Show Key view - display newly created secret key
  if (view === 'showKey' && newKeyData) {
    return (
      <VStack spacing={6} align="stretch" maxW="md" mx="auto">
        <Box textAlign="center">
          <Heading as="h1" size="lg" fontFamily="heading" mb={2}>
            Your Secret Key
          </Heading>
        </Box>

        <Alert status="warning" borderRadius="lg">
          <AlertIcon />
          <Box>
            <AlertTitle fontSize="sm">Save this key!</AlertTitle>
            <AlertDescription fontSize="sm">
              You won't be able to see it again once you leave this page.
            </AlertDescription>
          </Box>
        </Alert>

        <Card>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Box
                bg={colorMode === 'dark' ? 'stone.700' : 'stone.50'}
                borderRadius="lg"
                p={4}
                border="1px"
                borderColor={colorMode === 'dark' ? 'stone.600' : 'stone.200'}
              >
                <HStack justify="space-between" align="start">
                  <Text
                    fontFamily="mono"
                    fontSize="sm"
                    wordBreak="break-all"
                    flex={1}
                  >
                    {newKeyData.apiKey}
                  </Text>
                  <Button
                    size="sm"
                    onClick={onCopy}
                    leftIcon={<Icon as={hasCopied ? Check : Copy} boxSize={4} />}
                    variant="outline"
                    flexShrink={0}
                    ml={2}
                  >
                    {hasCopied ? 'Copied!' : 'Copy'}
                  </Button>
                </HStack>
              </Box>

              <Text fontSize="sm" color="stone.500">
                Use this key to access your account on other devices, via API, or with MCP.
              </Text>

              <Button
                colorScheme="brand"
                size="lg"
                w="full"
                onClick={handleSignUpComplete}
              >
                I've saved my key
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    )
  }

  // Dashboard view - show key stats and shares
  if (view === 'dashboard' && stats) {
    return (
      <VStack spacing={6} align="stretch">
        <Heading size="lg">Settings</Heading>

        {error && (
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            {error}
          </Alert>
        )}

        {/* Account Profile */}
        <Card>
          <CardHeader>
            <Heading size="md">Account Profile</Heading>
          </CardHeader>
          <CardBody pt={0}>
            <HStack spacing={4} align="center" justify="space-between">
              <HStack spacing={4}>
                <Box
                  p={3}
                  bg={getAvatarColor(apiKey)}
                  borderRadius="full"
                >
                  <Icon as={Ghost} boxSize={6} color="white" />
                </Box>
                <VStack align="start" spacing={1}>
                  {isEditingName ? (
                    <HStack spacing={2} w="full" maxW="300px">
                      <Input
                        size="sm"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        placeholder="Enter account name"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveName()
                          if (e.key === 'Escape') handleCancelEditName()
                        }}
                      />
                      <Button
                        size="sm"
                        colorScheme="brand"
                        onClick={handleSaveName}
                        isLoading={savingName}
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleCancelEditName}
                      >
                        Cancel
                      </Button>
                    </HStack>
                  ) : (
                    <HStack spacing={2}>
                      <Text fontWeight="medium" fontSize="lg" noOfLines={1} maxW="300px">
                        {stats.name || 'Anonymous'}
                      </Text>
                      <Button
                        size="xs"
                        variant="ghost"
                        leftIcon={<Icon as={Pencil} boxSize={3} />}
                        onClick={handleStartEditName}
                      >
                        Edit
                      </Button>
                    </HStack>
                  )}
                  <Text fontSize="sm" color="stone.500">
                    Anonymous account
                  </Text>
                </VStack>
              </HStack>
              <Button
                variant="outline"
                colorScheme="red"
                size="sm"
                onClick={onRevokeOpen}
                leftIcon={<Icon as={Trash2} boxSize={4} />}
              >
                Destroy Account
              </Button>
            </HStack>
          </CardBody>
        </Card>

        {/* Plan & Usage */}
        <Card>
          <CardHeader pb={2}>
            <HStack justify="space-between">
              <HStack spacing={3}>
                <Icon as={Zap} boxSize={5} />
                <HStack spacing={2} align="baseline">
                  <Heading size="md" textTransform="capitalize">
                    {stats.tier} Plan
                  </Heading>
                  {stats.tier === 'pro' && (
                    <Icon as={Check} boxSize={4} color="green.500" />
                  )}
                </HStack>
              </HStack>
              {stats.tier !== 'pro' && (
                <Button
                  as={RouterLink}
                  to="/#pricing"
                  variant="outline"
                  size="sm"
                  colorScheme="brand"
                >
                  View Plans
                </Button>
              )}
            </HStack>
          </CardHeader>
          <CardBody pt={0}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {/* Left: Storage Usage */}
              {stats.usage && (
                <Box
                  px={4}
                  py={3}
                  bg={colorMode === 'dark' ? 'stone.800' : 'stone.100'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'stone.600' : 'stone.200'}
                >
                  <Text fontWeight="semibold" fontSize="sm" color={colorMode === 'dark' ? 'stone.300' : 'stone.600'} mb={2}>
                    Storage Usage
                  </Text>
                  <VStack spacing={3} align="stretch">
                    <UsageBar
                      current={stats.usage.activeShares}
                      max={stats.usage.maxShares}
                      label="Active Shares"
                      icon={FileText}
                    />
                    <StorageBar
                      current={stats.usage.storageUsed}
                      max={stats.usage.maxStorage}
                    />
                  </VStack>
                </Box>
              )}

              {/* Right: Rate Limit Usage */}
              {stats.rateLimit && (
                <Box
                  px={4}
                  py={3}
                  bg={colorMode === 'dark' ? 'stone.800' : 'stone.100'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'stone.600' : 'stone.200'}
                >
                  <Text fontWeight="semibold" fontSize="sm" color={colorMode === 'dark' ? 'stone.300' : 'stone.600'} mb={2}>
                    API Rate Limits
                  </Text>
                  <VStack spacing={3} align="stretch">
                    <UsageBar
                      current={stats.rateLimit.dayUsed}
                      max={stats.rateLimit.dayMax}
                      label="Today's Requests"
                      icon={Activity}
                    />
                    <UsageBar
                      current={stats.rateLimit.minuteUsed}
                      max={stats.rateLimit.minuteMax}
                      label="This Minute"
                      icon={Clock}
                    />
                  </VStack>
                </Box>
              )}
            </SimpleGrid>

          </CardBody>
        </Card>

        {/* API Key */}
        <Card>
          <CardHeader pb={2}>
            <Heading size="md" display="flex" alignItems="center" gap={2}>
              <Icon as={Key} boxSize={5} />
              API Key
            </Heading>
          </CardHeader>
          <CardBody pt={0}>
            <VStack spacing={3} align="stretch">
              <HStack spacing={2}>
                <Box
                  flex={1}
                  bg={colorMode === 'dark' ? 'stone.800' : 'stone.100'}
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'stone.600' : 'stone.200'}
                  borderRadius="md"
                  px={3}
                  py={2}
                  fontFamily="mono"
                  fontSize="sm"
                >
                  {showSecretKey ? apiKey : maskSecretKey(apiKey)}
                </Box>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowSecretKey(!showSecretKey)}
                  aria-label={showSecretKey ? 'Hide key' : 'Show key'}
                >
                  <Icon as={showSecretKey ? EyeOff : Eye} boxSize={4} />
                </Button>
                <Button
                  size="sm"
                  onClick={onSecretKeyCopy}
                  leftIcon={<Icon as={hasSecretKeyCopied ? Check : Copy} boxSize={4} />}
                  variant="outline"
                >
                  {hasSecretKeyCopied ? 'Copied!' : 'Copy'}
                </Button>
              </HStack>
              <HStack spacing={6} fontSize="sm" color={colorMode === 'dark' ? 'stone.400' : 'stone.500'}>
                <Text>
                  Created {stats.createdAt ? formatDate(stats.createdAt) : 'N/A'}
                </Text>
                <Text>
                  Expires in{' '}
                  {stats.expiresAt ? (
                    <Text as="span" color="orange.500">{formatRelativeTime(stats.expiresAt)}</Text>
                  ) : (
                    'Never'
                  )}
                </Text>
                <Text>
                  Last used {stats.lastUsedAt ? formatDate(stats.lastUsedAt) : 'Never'}
                </Text>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* MCP Servers */}
        <Card>
          <CardHeader>
            <HStack justify="space-between">
              <Heading size="md" display="flex" alignItems="center" gap={2}>
                <Icon as={Cpu} boxSize={5} />
                MCP Servers
              </Heading>
              <Button
                as="a"
                href="/docs/mcp"
                target="_blank"
                variant="ghost"
                size="sm"
                rightIcon={<Icon as={ExternalLink} boxSize={3} />}
              >
                Docs
              </Button>
            </HStack>
          </CardHeader>
          <CardBody pt={0}>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="stone.500">
                Connect Myrkup to your AI assistant to create and manage encrypted markdown shares directly.
              </Text>

              <HStack spacing={3}>
                <CursorConnectButton variant="full" />
                <ClaudeConnectButton variant="full" />
              </HStack>

              {/* Connected Agents List */}
              {(agents.length > 0 || loadingAgents) && (
                <>
                  <Divider />
                  <VStack spacing={3} align="stretch">
                    <Text fontSize="sm" fontWeight="medium" color={colorMode === 'dark' ? 'stone.300' : 'stone.600'}>
                      Connected Agents
                    </Text>
                    {loadingAgents && agents.length === 0 ? (
                      <Text fontSize="sm" color="stone.500" textAlign="center" py={2}>
                        Loading connected agents...
                      </Text>
                    ) : (
                      agents.map((agent) => (
                        <AgentConnectionRow
                          key={agent.name}
                          type={
                            agent.name.toLowerCase().includes('cursor') ? 'cursor' :
                            agent.name.toLowerCase().includes('claude') ? 'claude' : 'other'
                          }
                          agent={agent}
                        />
                      ))
                    )}
                  </VStack>
                </>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Destroy Account Confirmation Modal */}
        <Modal isOpen={isRevokeOpen} onClose={onRevokeClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <HStack>
                <Icon as={AlertTriangle} color="red.500" />
                <Text>Destroy Account</Text>
              </HStack>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <Text>
                  Are you sure you want to destroy your account? This action cannot be undone.
                </Text>
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="medium">This will:</Text>
                    <List spacing={1} mt={2} fontSize="sm">
                      <ListItem>Permanently delete your anonymous account</ListItem>
                      <ListItem>Prevent creating new shares with this identity</ListItem>
                      <ListItem>Your existing shares will remain accessible</ListItem>
                    </List>
                  </Box>
                </Alert>
              </VStack>
            </ModalBody>
            <ModalFooter justifyContent="center">
              <Button variant="outline" mr={3} onClick={onRevokeClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleRevokeApiKey}
                isLoading={loading}
              >
                Destroy Account
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    )
  }

  // Loading state - waiting for stored key validation
  if (view === null) {
    return null
  }

  // Input view (default) - Anonymous Account
  return (
    <VStack spacing={6} align="stretch" maxW="md" mx="auto">
      <Box textAlign="center">
        <Heading as="h1" size="lg" fontFamily="heading" mb={2}>
          Welcome to Myrkup
        </Heading>
        <Text color="stone.500">
          Create an anonymous account to keep your Markdown in one place, or access an existing one with your secret key.
        </Text>
      </Box>

      {error && (
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          {error}
        </Alert>
      )}

      <Card>
        <CardHeader>
          <Heading size="md">Create Anonymous Account</Heading>
        </CardHeader>
        <CardBody pt={0}>
          <VStack spacing={4} align="stretch">
            <FormControl isInvalid={!!signUpError}>
              <FormLabel fontSize="sm">Account Name</FormLabel>
              <Input
                value={signUpName}
                onChange={(e) => {
                  setSignUpName(e.target.value)
                  setSignUpError(null)
                }}
                placeholder="e.g., john, design-team, acme-corp"
                onKeyDown={(e) => e.key === 'Enter' && handleSignUp()}
              />
              {signUpError && (
                <Text fontSize="xs" color="red.500" mt={1}>
                  {signUpError}
                </Text>
              )}
            </FormControl>
            <Text fontSize="xs" color="stone.500">
              This can be your name, team name, or organization. You can share this account with others by sharing the secret key.
            </Text>
            <Text fontSize="xs" color="stone.500">
              Anonymous accounts expire after 90 days. Link with a real account to make it permanent.
            </Text>
            <FormControl isInvalid={termsError}>
              <Flex align="flex-start" gap={3}>
                <Checkbox
                  isChecked={acceptedTerms}
                  onChange={(e) => {
                    setAcceptedTerms(e.target.checked)
                    setTermsError(false)
                  }}
                  colorScheme="brand"
                  sx={{
                    '& .chakra-checkbox__control': {
                      width: '18px',
                      height: '18px',
                      marginTop: '2px',
                    },
                  }}
                />
                <Box flex="1">
                  <Text as="label" fontSize="sm" lineHeight="1.6" display="block">
                    I have read and agree to the{' '}
                    <Link
                      as={RouterLink}
                      to="/terms"
                      color="brand.500"
                      textDecoration="underline"
                      _hover={{ color: 'brand.600' }}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                      Terms of Service
                    </Link>
                    {' '}and{' '}
                    <Link
                      as={RouterLink}
                      to="/privacy"
                      color="brand.500"
                      textDecoration="underline"
                      _hover={{ color: 'brand.600' }}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                      Privacy Policy
                    </Link>
                  </Text>
                </Box>
              </Flex>
              {termsError && (
                <FormErrorMessage fontSize="xs" ml="27px">
                  You must accept the Terms of Service to create an account
                </FormErrorMessage>
              )}
            </FormControl>
            <Button
              onClick={handleSignUp}
              isLoading={loading && !!signUpName.trim()}
              isDisabled={!signUpName.trim()}
              colorScheme="brand"
              size="lg"
              w="full"
            >
              Try Anonymously
            </Button>
            <HStack justify="center">
              <Text fontSize="sm" color="stone.500">or</Text>
              <Button
                variant="link"
                colorScheme="brand"
                size="sm"
                onClick={handleGetStarted}
                isLoading={loading && !signUpName.trim()}
              >
                skip naming for now
              </Button>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      <HStack>
        <Divider />
        <Text fontSize="sm" color="stone.500" whiteSpace="nowrap">or</Text>
        <Divider />
      </HStack>

      <Card>
        <CardHeader>
          <Heading size="md">Access Existing Account</Heading>
        </CardHeader>
        <CardBody pt={0}>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">Enter your secret key</FormLabel>
              <InputGroup>
                <InputLeftAddon fontFamily="mono" fontSize="sm">mk_</InputLeftAddon>
                <Input
                  value={inputKey.startsWith('mk_') ? inputKey.slice(3) : inputKey}
                  onChange={(e) => {
                    const val = e.target.value
                    setInputKey(val.startsWith('mk_') ? val : `mk_${val}`)
                  }}
                  placeholder="your-secret-key"
                  fontFamily="mono"
                  onKeyDown={(e) => e.key === 'Enter' && handleValidateKey()}
                />
              </InputGroup>
            </FormControl>
            <Text fontSize="xs" color="stone.500">
              Enter the secret key you received when creating your anonymous account.
            </Text>
            <Button
              onClick={handleValidateKey}
              isLoading={loading}
              isDisabled={!inputKey.trim()}
              colorScheme="brand"
              variant="outline"
              size="lg"
              w="full"
            >
              Access Account
            </Button>
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  )
}
